import { useEffect, useState, useRef } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

const MentionDropdown = ({ options, onSelect, onClose, disableActions }) => {
	const theme = useTheme();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const listRef = useRef(null);
	const selectedItemRef = useRef(null);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (options.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => (prev + 1) % options.length);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex(
						(prev) => (prev - 1 + options.length) % options.length
					);
					break;
				case 'Enter':
				case 'Tab':
					e.preventDefault();
					if (options[selectedIndex]) {
						onSelect(options[selectedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					if (onClose) onClose();
					break;
				default:
					break;
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [options, selectedIndex, onSelect, onClose]);

	// Scroll selected item into view when it changes
	useEffect(() => {
		if (selectedItemRef.current && listRef.current) {
			selectedItemRef.current.scrollIntoView({ block: 'nearest' });
		}
	}, [selectedIndex]);

	// Get color for entity type
	const getTypeColor = (type) => {
		const typeColors = {
			deal: theme.palette.mode === 'dark' ? '#8C8EFF' : '#8C8EFF',
			contact: theme.palette.mode === 'dark' ? '#ffab00' : '#ffab00',
			company: theme.palette.mode === 'dark' ? '#36b37e' : '#36b37e',
			default: theme.palette.primary.main,
		};
		return typeColors[type] || typeColors.default;
	};

	return (
		<ClickAwayListener onClickAway={() => { if (onClose) onClose(); }}>
			<Paper
				elevation={3}
				sx={{
					width: 340,
					minWidth: 320,
					maxHeight: '300px',
					overflow: 'auto',
					borderRadius: 1,
				}}
			>
				{options.length > 0 ? (
					<Box ref={listRef} sx={{ width: '100%' }}>
						{options.map((entity, index) => (
							<Box
								key={entity.id || index}
								ref={index === selectedIndex ? selectedItemRef : null}
								onClick={() => onSelect(entity)}
								onMouseEnter={() => setSelectedIndex(index)}
								sx={{
									padding: '8px 16px',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									backgroundColor:
										index === selectedIndex
											? theme.palette.action.selected
											: 'transparent',
									'&:hover': {
										backgroundColor: theme.palette.action.hover,
									},
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									width: '100%',
								}}
							>
								{entity.type === 'action' || entity.action === 'research' ? (
									<Box
										sx={{
											width: '14px',
											height: '14px',
											marginRight: 1.5,
											flexShrink: 0,
										}}
									>
										{entity.name === 'todo' || entity.id === 'todo' ? (
											<CheckBoxOutlinedIcon
												style={{
													fontSize: 14,
													color: theme.palette.text.primary,
												}}
											/>
										) : entity.name === 'reminder' ||
										  entity.id === 'reminder' ? (
											<NotificationsActiveOutlinedIcon
												style={{
													fontSize: 14,
													color: theme.palette.text.primary,
												}}
											/>
										) : (
											<SearchIcon
												style={{
													fontSize: 14,
													color: theme.palette.text.primary,
												}}
											/>
										)}
									</Box>
								) : (
									<Box
										sx={{
											width: '10px',
											height: '10px',
											borderRadius: '50%',
											backgroundColor: getTypeColor(entity.type),
											marginRight: 1.5,
											flexShrink: 0,
										}}
									/>
								)}
								<Box>
									<Tooltip title={entity.name} placement="right" arrow>
										<Typography
											variant='body1'
											color='textPrimary'
											sx={{
												maxWidth: 300,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												display: 'block',
											}}
										>
											{entity.name}
										</Typography>
									</Tooltip>
									{entity.description ? (
										<Typography variant='caption' color='textSecondary'>
											{entity.description.charAt(0).toUpperCase() + entity.description.slice(1)}
										</Typography>
									) : entity.action === 'research' ? (
										<Typography variant='caption' color='textSecondary'>
											{disableActions ? `No results found for "${entity.query}"` : 'Create a placeholder and begin research'}
										</Typography>
									) : (
										entity.type && (
											<Typography variant='caption' color='textSecondary'>
												{entity.type.charAt(0).toUpperCase() + entity.type.slice(1)}
											</Typography>
										)
									)}
								</Box>
							</Box>
						</Box>
					))}
				</Box>
				) : (
					<Box
						sx={{
							p: 2,
							color: theme.palette.text.secondary,
							textAlign: 'center',
						}}
					>
						Type to search...
					</Box>
				)}
			</Paper>
		</ClickAwayListener>
	);
};

export default MentionDropdown;
